import { useEffect, useRef } from "react";
import { config } from "@financial-advisory/config";
import { ContentType } from "@financial-advisory/data-access/api-types";
import {
  store,
  useAppSelector,
  useUiSwitcher,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  IHighlight,
  PdfHighlighter,
  PdfLoader,
} from "@financial-advisory/ui/components/pdf-viewer";
import "./PdfViewer.css";
import { TextHighlight } from "./TextHighlight";


interface PdfViewerProps {
  documentName: string;
  highlights: IHighlight[];
  scrollToPage?: number;
}

export const PdfViewer = ({
  documentName,
  highlights,
  scrollToPage,
}: PdfViewerProps) => {
  const isUncheckUser = useUiSwitcher();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrolledPageRef = useRef<number | undefined>(undefined);

  // Poll for the page element and scroll when found, continuing until page is visible
  useEffect(() => {
    if (!scrollToPage || !containerRef.current) return;
    if (lastScrolledPageRef.current === scrollToPage) return;

    let attempts = 0;
    const maxAttempts = 100;
    let cancelled = false;
    let observer: IntersectionObserver | null = null;

    const tryScroll = () => {
      if (cancelled) return;

      const pageElement = containerRef.current?.querySelector(
        `[data-page-number="${scrollToPage}"]`
      );

      if (pageElement) {
        // Use IntersectionObserver to detect when the page is actually visible
        observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (entry?.isIntersecting && entry.intersectionRatio > 0.1) {
              // Page is visible, mark scroll as complete
              lastScrolledPageRef.current = scrollToPage;
              observer?.disconnect();
              observer = null;
            }
          },
          {
            root: containerRef.current,
            threshold: 0.1,
          }
        );
        observer.observe(pageElement);

        // Trigger the scroll
        pageElement.scrollIntoView({ behavior: "smooth", block: "start" });

        // Keep checking and re-scrolling if needed until page is visible
        attempts++;
        if (attempts < maxAttempts && !cancelled) {
          setTimeout(() => {
            // If we haven't marked it complete yet, try scrolling again
            if (lastScrolledPageRef.current !== scrollToPage && !cancelled) {
              pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
              if (attempts < maxAttempts) {
                setTimeout(tryScroll, 200);
              }
            }
          }, 300);
        }
        return;
      }

      attempts++;
      if (attempts < maxAttempts && !cancelled) {
        setTimeout(tryScroll, 100);
      }
    };

    const timer = setTimeout(tryScroll, 100);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      observer?.disconnect();
    };
  }, [scrollToPage]);

  // Reset scroll tracking when document changes
  useEffect(() => {
    lastScrolledPageRef.current = undefined;
  }, [documentName]);

  if (!highlights) {
    return <div>Loading...</div>;
  }

  const url = isUncheckUser
    ? `${config.UNCHECK_SERVICE_API_BASE_URL}/regulations/${documentName}/source-document`
    : `/assets/full/${documentName}`;

  const httpHeaders = isUncheckUser && accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : undefined;

  return (
    <div ref={containerRef} className="h-full">
      <PdfLoader url={url} beforeLoad={<div>Loading...</div>} httpHeaders={httpHeaders}>
        {(pdfDocument) => (
          <PdfHighlighter
            store={store}
            pdfDocument={pdfDocument}
            highlightTransform={(highlight: IHighlight, index) => {
              return (
                <TextHighlight
                  key={index}
                  highlightId={highlight.id}
                  position={highlight.position}
                  contentType={highlight.contentType as ContentType}
                />
              );
            }}
            highlights={highlights}
          />
        )}
      </PdfLoader>
    </div>
  );
};
